import {
  whatsAppAgilePairingResponseSchema,
  whatsAppAllowedRecipientsResponseSchema,
  whatsAppChannelsSettingsSchema,
  whatsAppMetaSignupResponseSchema,
  whatsAppSenderPersonResponseSchema,
  whatsAppSendersResponseSchema,
  whatsAppStopLineResponseSchema,
  whatsAppTemplateResponseSchema,
  whatsAppTemplateSubmitResponseSchema,
  type SaveWhatsAppAllowedRecipientsBody,
  type SaveWhatsAppChannelsBody,
  type SetWhatsAppSenderBody,
  type UpdateWhatsAppStopLineBody,
  type UpdateWhatsAppTemplateBody,
  type WhatsAppAgilePairingResponse,
  type WhatsAppChannelsSettings,
  type WhatsAppMetaSignupBody,
  type WhatsAppSenderPerson,
  type WhatsAppSendersResponse,
  type WhatsAppTemplateKeyCode,
  type WhatsAppTemplatesResponse,
  type WhatsAppTemplateView,
} from "@gioia365/contracts";
import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../api/http";
import { callSequenceSettingsQuery, settingsQuery } from "../api/queries";
import type { TabContext } from "../session/tab-context";
import { whatsAppTemplatesQuery } from "./api";
import { whatsAppSettingsKeys } from "./settings-keys";

// I WhatsApp nelle Impostazioni (piano M6, R1, Task 5a; rotte del Task 3a, contratti in `packages/contracts/src/whatsapp.ts`):
// - il WhatsApp di ogni persona (`GET /api/whatsapp/senders`, `PUT /api/whatsapp/senders/:membershipId`), che leggono «Invio dei
//   WhatsApp» e la colonna «WhatsApp» del Team;
// - i WhatsApp che usa l'azienda (`GET`/`PUT /api/settings/whatsapp-channels`), con la versione delle impostazioni dell'azienda,
//   la stessa del calendario e della sequenza di chiamate;
// - i modelli e la riga «scrivi STOP» (`PUT /api/whatsapp/templates/:key`, `PUT /api/whatsapp/stop-line`), sulla lettura dei
//   modelli che usa anche la finestra del WhatsApp (`whatsAppTemplatesQuery`), e «Invia a Meta per l'approvazione» (`POST
//   /api/whatsapp/templates/:key/submit`, R2, Task 8b);
// - «Registra su Meta e salva» (`POST /api/whatsapp/meta-signup`, R2, Task 7b): la persona e il collegamento dell'azienda dopo;
// - il codice QR per collegare di nuovo il telefono di un WhatsApp Agile (`GET /api/whatsapp/senders/:membershipId/agile-pairing`,
//   R9, Task 27b): porta il codice di Agile, quindi non passa dalla cache di React Query; lo tiene soltanto la finestra «Imposta»;
// - i numeri ammessi di un'azienda, per il Super admin di Gioia365 (`GET`/`PUT /api/platform/organizations/:id/whatsapp/
//   allowed-recipients`, R2, Task 7b; questione 414), fuori dalla scheda come la telefonia di un'azienda.
// Ogni lettura sta sotto la scheda (`["scheda", tabKey(tab), …]`) e si toglie con le altre al cambio di azienda. Ogni scrittura
// riuscita mette subito la risposta nella copia in cache; riuscita o rifiutata (`409 version_conflict`), rilegge ciò che ha
// toccato, così la pagina mostra quello che il server ha davvero.

/** Le persone non archiviate con il loro WhatsApp, `canEdit` e, per ognuna, se chi guarda glielo può impostare (`canSet`). */
export function whatsAppSendersQuery(tab: TabContext) {
  return queryOptions({
    queryKey: whatsAppSettingsKeys.senders(tab),
    queryFn: ({ signal }) => apiRequest("GET", "/api/whatsapp/senders", { schema: whatsAppSendersResponseSchema, tab, signal }),
  });
}

/** Quali WhatsApp usa l'azienda («A mano» sempre), la versione delle impostazioni e se chi guarda la può cambiare. */
export function whatsAppChannelsQuery(tab: TabContext) {
  return queryOptions({
    queryKey: whatsAppSettingsKeys.channels(tab),
    queryFn: ({ signal }) => apiRequest("GET", "/api/settings/whatsapp-channels", { schema: whatsAppChannelsSettingsSchema, tab, signal }),
  });
}

/** La persona com'è dopo una scrittura, al suo posto nell'elenco in cache. */
function withPerson(current: WhatsAppSendersResponse | undefined, person: WhatsAppSenderPerson): WhatsAppSendersResponse | undefined {
  return current ? { ...current, people: current.people.map((candidate) => (candidate.membershipId === person.membershipId ? person : candidate)) } : current;
}

/**
 * «Salva» della finestra «Imposta»: la persona com'è dopo prende il suo posto nell'elenco, poi l'elenco si rilegge. Con WhatsApp Agile
 * (R9) il corpo porta il codice: la scrittura sparisce dalla cache appena la finestra si chiude (`gcTime: 0`), e la risposta non lo ha.
 */
export function useSetWhatsAppSender(tab: TabContext) {
  const queryClient = useQueryClient();
  const key = whatsAppSettingsKeys.senders(tab);
  return useMutation({
    gcTime: 0,
    mutationFn: ({ membershipId, body }: { membershipId: string; body: SetWhatsAppSenderBody }) =>
      apiRequest("PUT", `/api/whatsapp/senders/${encodeURIComponent(membershipId)}`, { schema: whatsAppSenderPersonResponseSchema, body, tab }),
    onSuccess: ({ person }) => queryClient.setQueryData<WhatsAppSendersResponse>(key, (current) => withPerson(current, person)),
    onSettled: () => void queryClient.invalidateQueries({ queryKey: key }),
  });
}

/**
 * Il codice QR per collegare di nuovo il telefono del WhatsApp Agile di una persona (R9, Task 27b), con quanti messaggi il fornitore ha
 * preso da quel numero nelle ultime 24 ore. Una lettura diretta, fuori dalla cache: il link porta il codice di Agile, che resta soltanto
 * nella finestra «Imposta» finché è aperta.
 */
export function readAgilePairing(tab: TabContext, membershipId: string, signal?: AbortSignal): Promise<WhatsAppAgilePairingResponse> {
  return apiRequest("GET", `/api/whatsapp/senders/${encodeURIComponent(membershipId)}/agile-pairing`, {
    schema: whatsAppAgilePairingResponseSchema,
    tab,
    ...(signal ? { signal } : {}),
  });
}

/**
 * «Registra su Meta e salva» e «Riprova»: senza codice, oppure con il codice e il WhatsApp Business Account della finestra di Meta.
 * Riuscita, la persona prende il suo posto nell'elenco e il collegamento dell'azienda quello del riquadro di Meta; riuscita o
 * rifiutata, si rileggono l'elenco (un passo non riuscito lascia il WhatsApp della persona `failed`, con una versione nuova) e i
 * WhatsApp dell'azienda (il riquadro «Business Manager di Meta» e il conteggio dei numeri registrati).
 */
export function useRegisterOnMeta(tab: TabContext) {
  const queryClient = useQueryClient();
  const senders = whatsAppSettingsKeys.senders(tab);
  const channels = whatsAppSettingsKeys.channels(tab);
  return useMutation({
    mutationFn: (body: WhatsAppMetaSignupBody) => apiRequest("POST", "/api/whatsapp/meta-signup", { schema: whatsAppMetaSignupResponseSchema, body, tab }),
    onSuccess: ({ person, connection }) => {
      queryClient.setQueryData<WhatsAppSendersResponse>(senders, (current) => withPerson(current, person));
      queryClient.setQueryData<WhatsAppChannelsSettings>(channels, (current) => (current ? { ...current, metaSignup: { ...current.metaSignup, connection } } : current));
    },
    onSettled: () => {
      for (const queryKey of [senders, channels]) void queryClient.invalidateQueries({ queryKey });
    },
  });
}

/**
 * Accende o spegne i WhatsApp automatici dell'azienda. La versione è quella delle impostazioni dell'azienda: si rileggono anche il
 * calendario e la sequenza di chiamate; e chi usava un WhatsApp spento passa a mano, quindi si rilegge l'elenco delle persone.
 */
export function useSaveWhatsAppChannels(tab: TabContext) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: SaveWhatsAppChannelsBody) => apiRequest("PUT", "/api/settings/whatsapp-channels", { schema: whatsAppChannelsSettingsSchema, body, tab }),
    onSuccess: (response) => queryClient.setQueryData(whatsAppSettingsKeys.channels(tab), response),
    onSettled: () => {
      for (const queryKey of [whatsAppSettingsKeys.channels(tab), whatsAppSettingsKeys.senders(tab), settingsQuery(tab).queryKey, callSequenceSettingsQuery(tab).queryKey]) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

/** Il modello com'è dopo una scrittura, al suo posto fra i sei in cache. */
function withTemplate(current: WhatsAppTemplatesResponse | undefined, template: WhatsAppTemplateView): WhatsAppTemplatesResponse | undefined {
  return current ? { ...current, templates: current.templates.map((candidate) => (candidate.key === template.key ? template : candidate)) } : current;
}

/** «Salva» di un modello: nome e testo con la versione letta; il modello com'è dopo prende il suo posto fra i sei. */
export function useUpdateWhatsAppTemplate(tab: TabContext) {
  const queryClient = useQueryClient();
  const { queryKey } = whatsAppTemplatesQuery(tab);
  return useMutation({
    mutationFn: ({ key, body }: { key: WhatsAppTemplateKeyCode; body: UpdateWhatsAppTemplateBody }) =>
      apiRequest("PUT", `/api/whatsapp/templates/${encodeURIComponent(key)}`, { schema: whatsAppTemplateResponseSchema, body, tab }),
    onSuccess: ({ template }) => queryClient.setQueryData<WhatsAppTemplatesResponse>(queryKey, (current) => withTemplate(current, template)),
    onSettled: () => void queryClient.invalidateQueries({ queryKey }),
  });
}

/**
 * «Invia a Meta per l'approvazione» di un modello, il testo salvato (nessun corpo): il modello com'è dopo prende il suo posto fra i
 * sei; riuscito o rifiutato (un errore di Meta lascia lo stato com'era, con il codice in `metaError`), i modelli si rileggono.
 */
export function useSubmitWhatsAppTemplate(tab: TabContext) {
  const queryClient = useQueryClient();
  const { queryKey } = whatsAppTemplatesQuery(tab);
  return useMutation({
    mutationFn: (key: WhatsAppTemplateKeyCode) =>
      apiRequest("POST", `/api/whatsapp/templates/${encodeURIComponent(key)}/submit`, { schema: whatsAppTemplateSubmitResponseSchema, tab }),
    onSuccess: ({ template }) => queryClient.setQueryData<WhatsAppTemplatesResponse>(queryKey, (current) => withTemplate(current, template)),
    onSettled: () => void queryClient.invalidateQueries({ queryKey }),
  });
}

/** «Salva» della riga «scrivi STOP», con la sua versione. */
export function useUpdateWhatsAppStopLine(tab: TabContext) {
  const queryClient = useQueryClient();
  const { queryKey } = whatsAppTemplatesQuery(tab);
  return useMutation({
    mutationFn: (body: UpdateWhatsAppStopLineBody) => apiRequest("PUT", "/api/whatsapp/stop-line", { schema: whatsAppStopLineResponseSchema, body, tab }),
    onSuccess: ({ stopLine, stopLineVersion }) =>
      queryClient.setQueryData<WhatsAppTemplatesResponse>(queryKey, (current) => (current ? { ...current, stopLine, stopLineVersion } : current)),
    onSettled: () => void queryClient.invalidateQueries({ queryKey }),
  });
}

const allowedRecipientsPath = (organizationId: string) => `/api/platform/organizations/${encodeURIComponent(organizationId)}/whatsapp/allowed-recipients`;

/**
 * I numeri ammessi dei WhatsApp automatici di un'azienda, la versione del suo collegamento WhatsApp e se in questo ambiente sono
 * richiesti (`required`: lo stesso per tutte le aziende, perché dipende dall'ambiente del server).
 */
export function platformWhatsAppAllowedRecipientsQuery(organizationId: string) {
  return queryOptions({
    queryKey: ["piattaforma", "whatsapp", "numeri-ammessi", organizationId] as const,
    queryFn: ({ signal }) => apiRequest("GET", allowedRecipientsPath(organizationId), { schema: whatsAppAllowedRecipientsResponseSchema, signal }),
  });
}

/** «Salva» dei numeri ammessi, con la versione letta: la risposta prende il posto della lettura, che poi si rilegge. */
export function useSaveWhatsAppAllowedRecipients(organizationId: string) {
  const queryClient = useQueryClient();
  const { queryKey } = platformWhatsAppAllowedRecipientsQuery(organizationId);
  return useMutation({
    mutationFn: (body: SaveWhatsAppAllowedRecipientsBody) =>
      apiRequest("PUT", allowedRecipientsPath(organizationId), { schema: whatsAppAllowedRecipientsResponseSchema, body }),
    onSuccess: (response) => queryClient.setQueryData(queryKey, response),
    onSettled: () => void queryClient.invalidateQueries({ queryKey }),
  });
}
